import React, { ReactElement } from 'react'
import styled, { css } from 'styled-components'
import { VersionItemLink } from './VersionItemLink'
import Tooltip from 'components/Tooltip'
import Caption from 'components/Caption'
import { VersionItemHeading, HeadingContainer } from './VersionItemHeading'
import VersionIcon from './VersionIcon'
import { RevisionIcon } from './RevisionIcon'
import VersionMeta from 'components/VersionMeta/VersionMeta'
import { containerStyles } from 'modules/versioning/components/ShareVersionItemOptions'
import { isNewStarMoreRecent } from 'modules/versioning/utils'
// Usage of RenderStatusType will not break the build
// eslint-disable-next-line no-restricted-imports
import { RenderStatusType, Kind } from 'gqlTypes/expansiveTypes'
import { VersionListItemFragment } from 'gqlTypes'
import { VersionType } from './types'

export const StyledTooltip = styled(Tooltip)`
  display: flex;
  align-items: center;
  width: 100%;
`

interface VersionItemProps {
  type: VersionType
  active?: boolean
  disabled?: boolean
  pathname?: string
  tooltip?: ReactElement | string
  creator: string
  date: Date
  userIsCreator: boolean
  actions?: React.ReactElement
  renderStatus?: RenderStatusType
  kind: Kind
  starOnHover?: boolean
  /* Only needed if starOnHover is true*/
  identifier?: string
  createdAt?: string
  isLibrary?: boolean
  latestStar?: VersionListItemFragment
  isFirst?: boolean
}

// Styles
const Container = styled.div<{
  active?: boolean
  deleted?: boolean
}>(
  ({ theme, active, deleted }) => css`
    display: flex;
    align-items: center;
    height: 32px;
    margin: 0 4px 1px; /* stylelint-disable-line scales/space */
    transition: color ${theme.transitions.duration[1]},
      background-color ${theme.transitions.duration[1]};
    border-radius: 3px;
    padding-left: 16px;
    padding-right: 16px;

    ${containerStyles};

    ${Caption} {
      color: ${theme.colors.silverchalice};
    }

    /* Active version */
    ${active &&
    css`
      ${HeadingContainer} {
        color: ${theme.colors.primaryTint[100]};
      }

      ${Caption} {
        color: ${theme.colors.primaryTint[100]};
      }

      background: ${theme.colors.primaryTint[10]};
    `}

    /* Inactive version */
    ${!active &&
    css`
      ${HeadingContainer} {
        color: ${theme.colors.abbey};
      }
    `}

    /* Deleted version */
    ${deleted &&
    css`
      ${HeadingContainer}, ${Caption} {
        color: ${theme.colors.silverchalice};
      }
    `}

    /* Hover styles */
    :hover {
      background-color: ${active
        ? theme.colors.primaryTint[30]
        : theme.colors.concrete};
    }
  `
)

const ItemContainer = styled.div`
  width: 19px;
  height: 19px;
  display: flex;
  justify-content: center;
  align-content: center;
  flex-shrink: 0;
`

const VersionItem = ({
  type,
  active,
  disabled,
  pathname,
  tooltip,
  creator,
  date,
  userIsCreator,
  actions,
  renderStatus,
  kind,
  starOnHover = false,
  identifier = '',
  createdAt = '',
  latestStar,
  isLibrary = false,
  isFirst,
}: VersionItemProps) => {
  const getPublishedVersionCopy = () =>
    kind === 'PUBLISHED' ? ' (Starred)' : null

  const revisionIconProps = {
    versionIdentifier: identifier,
    kind,
    isLibrary,
    isNewLatest: isNewStarMoreRecent(latestStar, createdAt),
  }

  return (
    <Container
      active={active}
      deleted={type === 'deleted'}
      data-testid="version-item">
      <StyledTooltip
        spacing="14px"
        disabled={!tooltip}
        content={tooltip}
        placement="bottom">
        <VersionItemLink
          isDisabled={disabled || type === 'deleted' || type === 'unavailable'}
          data-testid="version-item-link"
          to={pathname}>
          <ItemContainer>
            <VersionIcon
              active={active}
              type={type}
              revisionIcon={
                <RevisionIcon
                  isActive={active}
                  starOnHover={starOnHover}
                  isFirst={isFirst}
                  {...revisionIconProps}
                />
              }
            />
          </ItemContainer>
          <VersionItemHeading emphasize={userIsCreator}>
            {userIsCreator ? 'You' : creator}
          </VersionItemHeading>
          <VersionMeta date={date} renderStatus={renderStatus}>
            {getPublishedVersionCopy()}
            {type === 'deleted' && ' (Deleted)'}
          </VersionMeta>
          {actions}
        </VersionItemLink>
      </StyledTooltip>
    </Container>
  )
}

export default VersionItem
